import { useRouter } from 'next/router'

import { SignUpScenario, RegisterFields } from '~/types/sign-up'
import { UserField } from '~/types/user'
import { getBrowserTimezone, getCountryCodesFromPhoneNumber } from '~/utils/helpers'
import useCurrentUser from '~/hooks/useCurrentUser'
import { UserRole } from '~/types/user-info-types'
import { useAppSelector } from '~/redux/hooks'
import { selectUsOnboarding } from '~/onboarding/redux/onboarding.slice'
import { UsOnboardingOnboardingStep } from '~/get-started/us-onboarding/consts/types'
import { QuestionStructure } from '~/onboarding/onboarding.types'

const DEFAULT_FIELD_VALUES: RegisterFields = {
  [UserField.AVATAR_PNG]: '',
  [UserField.AVATAR_GLB]: '',
  [UserField.BIRTH_YEAR]: '',
  [UserField.COUNTRY]: '',
  [UserField.COUNTRY_CALLING_CODE]: '',
  [UserField.COUNTRY_CODE]: '',
  [UserField.EMAIL]: '',
  [UserField.FACEBOOK_EMAIL]: '',
  [UserField.AUTH_EMAIL]: '',
  [UserField.FULL_NAME]: '',
  [UserField.GRADE_LEVEL_ID]: undefined,
  [UserField.NICKNAME]: '',
  [UserField.PASSWORD]: '',
  [UserField.PASSWORD_CONFIRM]: '',
  [UserField.PHONE]: '',
  [UserField.SKILL]: undefined,
  [UserField.TIMEZONE]: '',
  [UserField.TYPE]: undefined,
  [UserField.WANTS_NEWSLETTER]: false,
  [UserField.YEARS_IN_SCHOOL]: undefined,
}

const initialUser = {
  avatarPngUrl: null,
  avatarUrl: null,
  birthYear: '',
  country: null,
  createdAt: '',
  email: null,
  emailVerified: false,
  facebookEmail: null,
  facebookId: null,
  authEmail: null,
  fullName: '',
  gradeLevelId: null,
  id: 0,
  nickname: '',
  phone: '',
  passwordReset: false,
  role: UserRole.USER,
  skill: null,
  timezone: '',
  token: '',
  userType: null,
  wantsNewsletter: false,
  yearsInSchool: null,

  /** @deprecated */
  birthdate: null,
  feideUid: null,
  firstName: null,
  gradeLevelAt: null,
  lastName: null,
  school: null,
  wantsMultgame: false,
  zipcode: null,
}

export default function useHydrateRegisterFields(signUpScenario: SignUpScenario): RegisterFields {
  const user = useCurrentUser() ?? initialUser
  const router = useRouter()
  const { email }: { email?: string } = router.query
  const emailFromQueryParam = email ?? ''

  // Get email from onboarding flow
  const usOnboarding = useAppSelector(selectUsOnboarding)
  const emailFromOnboarding = (
    usOnboarding?.steps?.[UsOnboardingOnboardingStep.ALMOST_THERE] as QuestionStructure | undefined
  )?.answer

  const [countryCallingCode, countryCode] = getCountryCodesFromPhoneNumber(user.phone)
  const countryCodes = {
    countryCallingCode,
    countryCode,
  }

  switch (signUpScenario) {
    case SignUpScenario.COMPLETE_PROFILE:
      return {
        [UserField.BIRTH_YEAR]: user.birthYear,
        [UserField.COUNTRY]: user.country,
        [UserField.EMAIL]: user.email,
        [UserField.FACEBOOK_EMAIL]: user.facebookEmail,
        [UserField.AUTH_EMAIL]: user.authEmail,
        [UserField.NICKNAME]: user.nickname,
        [UserField.GRADE_LEVEL_ID]: user.gradeLevelId,
        [UserField.SKILL]: user.skill,
        [UserField.TYPE]: user.userType,
        [UserField.YEARS_IN_SCHOOL]: user.yearsInSchool,
        ...countryCodes,
      }

    case SignUpScenario.DEFAULT:
      return {
        ...DEFAULT_FIELD_VALUES,
        ...countryCodes,
        [UserField.COUNTRY]: user.country,
        [UserField.EMAIL]: emailFromOnboarding || emailFromQueryParam,
      }

    case SignUpScenario.SUBSCRIPTION:
      return {
        ...DEFAULT_FIELD_VALUES,
        [UserField.BIRTH_YEAR]: user.birthYear,
        [UserField.NICKNAME]: user.nickname,
        [UserField.COUNTRY]: user.country,
        [UserField.PHONE]: String(user.phone).split(':')[1],
        [UserField.EMAIL]: emailFromOnboarding || user.email || user.facebookEmail || user.authEmail,
        ...countryCodes,
      }

    case SignUpScenario.CHECKOUT_FOR_OTHER:
      return {
        [UserField.BIRTH_YEAR]: user.birthYear,
        [UserField.COUNTRY]: user.country,
        [UserField.EMAIL]: user.email ?? emailFromQueryParam,
        [UserField.FACEBOOK_EMAIL]: user.facebookEmail,
        [UserField.AUTH_EMAIL]: user.authEmail,
        [UserField.FULL_NAME]: user.fullName,
        [UserField.NICKNAME]: user.nickname,
        [UserField.PHONE]: String(user.phone).split(':')[1],
        [UserField.TIMEZONE]: user.timezone ?? getBrowserTimezone(),
        [UserField.WANTS_NEWSLETTER]: user.wantsNewsletter,
        ...countryCodes,
      }

    case SignUpScenario.CHECKOUT_FOR_SELF:
      return {
        [UserField.BIRTH_YEAR]: user.birthYear,
        [UserField.COUNTRY]: user.country,
        [UserField.EMAIL]: user.email ?? emailFromQueryParam,
        [UserField.FACEBOOK_EMAIL]: user.facebookEmail,
        [UserField.AUTH_EMAIL]: user.authEmail,
        [UserField.FULL_NAME]: user.fullName,
        [UserField.NICKNAME]: user.nickname,
        [UserField.PHONE]: String(user.phone).split(':')[1],
        [UserField.SKILL]: user.skill,
        [UserField.TIMEZONE]: user.timezone ?? getBrowserTimezone(),
        [UserField.WANTS_NEWSLETTER]: user.wantsNewsletter,
        [UserField.YEARS_IN_SCHOOL]: user.yearsInSchool,
        ...countryCodes,
      }

    case SignUpScenario.MISSING_REQUIRED_FIELDS:
      return {
        [UserField.EMAIL]: user.email,
        [UserField.FACEBOOK_EMAIL]: user.facebookEmail,
        [UserField.AUTH_EMAIL]: user.authEmail,
        [UserField.NICKNAME]: user.nickname,
        [UserField.WANTS_NEWSLETTER]: user.wantsNewsletter,
        ...countryCodes,
      }

    default:
      return DEFAULT_FIELD_VALUES
  }
}
